//src/Components/Survey/Pages/UserManagement/AssignAssessment/Share.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {Search,ChevronLeft,ChevronRight,Inbox,Share2,} from "lucide-react";
import { GetRelatedCompanyUser,SharedAssessment  } from "../../../Services/SurveyService";
import SurveyNavTabs from "../../../../../Utility/SurveyNavTabs";
import { sanitizeHtml, stripHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../../../Utility/sanitizeHtml";
import api from "../../../../../Components/axios";

const PAGE_SIZE = 10;


const rawBaseURL = api.defaults.baseURL || "";
const IMAGE_BASE_URL = rawBaseURL.endsWith("/") ? rawBaseURL : `${rawBaseURL}/`;

const initials = (name) =>
    name ? name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() : "";

const UserAvatar = ({ name, profileImageUrl }) => {
    const [imgError, setImgError] = useState(false);
    const src = profileImageUrl ? `${IMAGE_BASE_URL}${profileImageUrl}` : null;

    if (src && !imgError) {
        return (
            <img
                src={src}
                alt={name}
                onError={() => setImgError(true)}
                className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
            />
        );
    }

    return (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#c2a8f033] text-xs font-semibold text-[#7f35b2]">
            {initials(name)}
        </div>
    );
};

const TableRowSkeleton = () => (
    <tr className="border-b border-gray-50 animate-pulse">
        <td className="py-3.5 pr-3">
            <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 shrink-0 rounded-full bg-gray-200" />
                <div className="h-4 w-28 rounded bg-gray-200" />
            </div>
        </td>
        <td className="py-3.5 pr-3"><div className="h-4 w-36 rounded bg-gray-200" /></td>
        <td className="py-3.5 pr-3"><div className="h-6 w-16 rounded-full bg-gray-200" /></td>
        <td className="py-3.5 pr-3"><div className="h-6 w-16 rounded-full bg-gray-200" /></td>
    </tr>
);

const Share = () => {
    const { surveyUID: surveyUIDFromParams } = useParams();
    const location = useLocation();
    const {
        surveyUID = surveyUIDFromParams,
        surveyName,
        surveyDescription,
        viewOnly = false,
        surveyStatus,
    } = location.state || {};

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    // Per-user permission map: { [userUid]: { canView: bool, canEdit: bool } }
    const [permissions, setPermissions] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [sharing, setSharing] = useState(false);

    useEffect(() => {
        if (!surveyUID) return;

        const fetchUsers = async () => {
            try {
                setLoading(true);
                const response = await GetRelatedCompanyUser(surveyUID);
                if (response?.data?.status === "success") {
                    const list = response?.data?.data || [];
                    setUsers(list);

                    const initialPermissions = {};
                    list.forEach((user) => {
                        initialPermissions[user.userUid] = {
                            canView: !!user.canView,
                            canEdit: !!user.canEdit,
                        };
                    });
                    setPermissions(initialPermissions);
                } else {
                    CustomToast("fail", "Something went wrong please try again later");
                }
            } catch (error) {
                CustomToast("fail", "An error occurred while fetching the Users");
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveyUID]);

    const filteredUsers = useMemo(() => {
        return users.filter((user) => {
            const matchesSearch =
                !search ||
                user.name?.toLowerCase().includes(search.toLowerCase()) ||
                user.email?.toLowerCase().includes(search.toLowerCase());
            return matchesSearch;
        });
    }, [users, search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const paginatedUsers = filteredUsers.slice(startIndex, startIndex + PAGE_SIZE);

    const togglePermission = (userUid, field) => {
        setPermissions((prev) => {
            const current = prev[userUid] || { canView: false, canEdit: false };
            const newValue = !current[field];

            const updated = { ...current, [field]: newValue };

            // Selecting "Can Edit" implies "Can View" as well.
            if (field === "canEdit" && newValue === true) {
                updated.canView = true;
            }

            return {
                ...prev,
                [userUid]: updated,
            };
        });
    };

    const changedCount = useMemo(() => {
        return users.reduce((count, user) => {
            const current = permissions[user.userUid] || { canView: false, canEdit: false };
            const original = { canView: !!user.canView, canEdit: !!user.canEdit };
            const changed = current.canView !== original.canView || current.canEdit !== original.canEdit;
            return changed ? count + 1 : count;
        }, 0);
    }, [users, permissions]);

        const handleShareSelected = async () => {
        const updates = users
            .filter((user) => {
                const current = permissions[user.userUid] || { canView: false, canEdit: false };
                const original = { canView: !!user.canView, canEdit: !!user.canEdit };
                return current.canView !== original.canView || current.canEdit !== original.canEdit;
            })
            .map((user) => ({
                targetUserUID: user.userUid,
                canView: permissions[user.userUid]?.canView || false,
                canEdit: permissions[user.userUid]?.canEdit || false,
            }));

        if (updates.length === 0) return;

        try {
            setSharing(true);
            const response = await SharedAssessment({
                surveyUID: surveyUID,
                targetUsers: updates,
            });
            if (response?.data?.status === "success") {
                CustomToast("success", response?.data?.message || "Access updated successfully.");
                setUsers((prev) =>
                    prev.map((user) => {
                        const change = updates.find((u) => u.targetUserID === user.userUid);
                        return change ? { ...user, canView: change.canView, canEdit: change.canEdit } : user;
                    })
                );
            } else {
                CustomToast("fail", response?.data?.message || "Unable to update access.");
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "An error occurred while sharing.");
        } finally {
            setSharing(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#f7f8fa]">
            <div className="mx-auto max-w-[1440px] py-2">
                <SurveyNavTabs
                    active="share"
                    surveyUID={surveyUID}
                    surveyName={surveyName}
                    surveyDescription={surveyDescription}
                    viewOnly={viewOnly}
                    surveyStatus={surveyStatus}
                />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mx-auto max-w-[1440px] pb-6"
            >
                {surveyName && (
                    <div className="mb-4 rounded-2xl bg-gradient-to-br from-[#7f35b2]/10 to-[#7f35b2]/5 p-4">
                        <h2 className="text-base font-semibold text-gray-900">{surveyName}</h2>
                        {surveyDescription && (
                            <>
                                <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                                <div
                                    className="description-rich-content mt-1 text-xs text-gray-600 line-clamp-2"
                                    title={stripHtml(surveyDescription)}
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(surveyDescription) }}
                                />
                            </>
                        )}
                    </div>
                )}

                <div className="mb-4">
                    <h1 className="text-2xl font-bold text-gray-900">Share</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Set view or edit access for each user on this assessment.
                    </p>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-md">
                    {/* Search */}
                    <div className="mb-4 flex justify-end">
                        <div className="relative w-full max-w-xs">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 " />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name or email..."
                                className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-[#7f35b2] focus:ring-1 focus:ring-[#7f35b2]"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-400">
                                    <th className="py-3 pr-3">User</th>
                                    <th className="py-3 pr-3">Email</th>
                                    {!viewOnly && (
                                        <>
                                            <th className="py-3 pr-3 text-center">Can View</th>
                                            <th className="py-3 pr-3 text-center">Can Edit</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, idx) => <TableRowSkeleton key={idx} />)
                                ) : paginatedUsers.length === 0 ? (
                                    <tr>
                                       <td colSpan={viewOnly ? "2" : "4"} className="py-12 text-center text-gray-400">
                                            <div className="flex flex-col items-center justify-center gap-2">
                                                <Inbox size={32} className="text-gray-300" />
                                                <p className="text-sm font-medium">No users found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedUsers.map((user) => {
                                        const perm = permissions[user.userUid] || { canView: false, canEdit: false };
                                        return (
                                            <tr
                                                key={user.userUid}
                                                className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="py-3 pr-3">
                                                    <div className="flex items-center gap-2.5">
                                                        <UserAvatar name={user.name} profileImageUrl={user.profileImageUrl} />
                                                        <span className="font-medium text-gray-900">{user.name}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 pr-3 text-gray-500">{user.email}</td>
                                                {!viewOnly && (
                                                    <>
                                                        <td className="py-3 pr-3 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={perm.canView}
                                                                disabled={perm.canEdit}
                                                                onChange={() => togglePermission(user.userUid, "canView")}
                                                                aria-label={`Can view for ${user.name}`}
                                                                className="h-4 w-4 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                                            />
                                                        </td>
                                                        <td className="py-3 pr-3 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={perm.canEdit}
                                                                onChange={() => togglePermission(user.userUid, "canEdit")}
                                                                aria-label={`Can edit for ${user.name}`}
                                                                className="h-4 w-4 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2] cursor-pointer"
                                                            />
                                                        </td>
                                                    </>
                                                )} 
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer: selection summary / pagination + share action */}
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                        <p className="text-gray-500">
                            {changedCount > 0 ? (
                                <span className="text-[#7f35b2] font-medium">{changedCount} user(s) with unsaved changes</span>
                            ) : (
                                `Showing ${paginatedUsers.length > 0 ? startIndex + 1 : 0} to ${startIndex + paginatedUsers.length} of ${filteredUsers.length} users`
                            )}
                        </p>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <ChevronLeft size={15} />
                                </button>
                                <span className="px-2 text-sm text-gray-600">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    <ChevronRight size={15} />
                                </button>
                            </div>

                            {!viewOnly && (
                                <button
                                    onClick={handleShareSelected}
                                    disabled={changedCount === 0 || sharing}
                                    className="flex items-center gap-2 rounded-lg bg-[#7f35b2] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#3bb290] disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                                >
                                    <Share2 size={16} />
                                    {sharing ? "Saving..." : `Save Access${changedCount > 0 ? ` (${changedCount})` : ""}`}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default Share;
